<template>
    <ListingSettingsHeader
        :displayTotalResults="!isTableLoading"
        :totalResults="totalActivities"
        :filters="filtersEnabled"
        :columns="columns"
        :enableColumnsSettings="true"
        @selectFilter="updateActiveFilters"
        @updateColumns="(newColumns) => $emit('updateColumns', newColumns)"
    >
        <template #filters>
            <component
                v-bind="activeFilter"
                :is="activeFilter.component"
                v-for="activeFilter in getActiveFilters"
                :key="activeFilter.code"
                class="listing-table-filters__item"
                :filter="activeFilter"
                @update:displayDropdown="
                    (value) => {
                        activeFilter.displayDropdown = value;
                    }
                "
                @updateFilter="
                    (values) => {
                        activeFilter.callback(values, activeFilter);
                    }
                "
                @updateFilterOptions="
                    (options) => {
                        activeFilter.optionsSelected = options;
                    }
                "
                @removeFilter="removeActiveFilter(activeFilter)"
            />
        </template>
    </ListingSettingsHeader>
</template>
<script>
import ListingSettingsHeader from 'vcomponents/common/listing/header/ListingSettingsHeader.vue';
import ListingFiltersSelect from 'vcomponents/common/listing/filters/types/ListingFiltersSelect.vue';
import ListingFiltersDate from 'vcomponents/common/listing/filters/types/ListingFiltersDate.vue';
import ListingFiltersSelectUsersWithCheckbox from 'vcomponents/common/listing/filters/types/users/ListingFiltersSelectUsersWithCheckbox.vue';
import Feature from 'Services/Feature';
import { markRaw } from 'vue';
import {
    yesterday,
    today,
    tomorrow,
    last_week,
    current_week,
    next_week,
    last_month,
    current_month,
} from 'Helpers/activities/activitiesDateFilters';

import { isEqual, has } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
import {
    initializeMetasLabels,
    initLabelsFilter,
    getSelectedLabels,
} from 'Helpers/activities/activitiesLabelsFilterHelper';
const userMapState = createNamespacedHelpers('user').mapState;
const { mapActions, mapGetters: activitiesMapGetters } =
    createNamespacedHelpers('activitiesStores');
import { TABS } from 'Dictionaries/activities/ActivitiesEnums.js';

export default {
    components: {
        ListingSettingsHeader,
        ListingFiltersSelect: markRaw(ListingFiltersSelect),
        ListingFiltersDate: markRaw(ListingFiltersDate),
        ListingFiltersSelectUsersWithCheckbox: markRaw(ListingFiltersSelectUsersWithCheckbox),
    },
    props: {
        columns: {
            type: Array,
            default: () => [],
        },
        activeTab: {
            type: String,
            required: true,
        },
    },
    emits: ['updateColumns'],
    data() {
        return {
            metasLabels: {},
            staffList: [],
            filters: [
                {
                    optionsSelected: [],
                    value: ['task', 'phonecall'],
                    label: this.$l10n.__("Par type d'activité"),
                    activeLabel: this.$l10n.__("Type d'activité"),
                    code: 'entities',
                    multiple: true,
                    applyLabel: this.$l10n.__('Appliquer'),
                    isActive: false,
                    options: [
                        { label: this.$l10n.__('Tâches'), id: 'task', icon: 'obj-slsy-task' },
                        { label: this.$l10n.__('Appels'), id: 'phonecall', icon: 'obj-slsy-phone' },
                    ],
                    component: ListingFiltersSelect,
                    callback: this.updateFilterValues,
                    displayDropdown: false,
                },
                {
                    optionsSelected: null,
                    label: this.$l10n.__('Par date'),
                    activeLabel: this.$l10n.__('Date'),
                    code: 'due_date',
                    isActive: false,
                    component: ListingFiltersDate,
                    callback: this.onDateSelected,
                    displayDropdown: false,
                    value: null,
                    datePickerValues: [],
                    filterConfig: {
                        input: false,
                        range: true,
                        calendarOptions: {
                            shortcuts: [
                                yesterday,
                                today,
                                tomorrow,
                                last_week,
                                current_week,
                                next_week,
                                last_month,
                                current_month,
                            ],
                        },
                    },
                },
                {
                    optionsSelected: [],
                    value: [],
                    label: this.$l10n.__('Par collaborateur'),
                    activeLabel: this.$l10n.__('Collaborateur'),
                    code: 'staffs',
                    applyLabel: this.$l10n.__('Appliquer'),
                    isActive: false,
                    options: {},
                    component: ListingFiltersSelectUsersWithCheckbox,
                    callback: this.updateFilterValues,
                    displayDropdown: false,
                },
                {
                    optionsSelected: [],
                    label: this.$l10n.__('Par label'),
                    activeLabel: this.$l10n.__('Label'),
                    code: 'labels',
                    isActive: false,
                    value: {},
                    multiple: true,
                    applyLabel: this.$l10n.__('Appliquer'),
                    filterable: true,
                    options: [],
                    component: ListingFiltersSelect,
                    callback: this.onLabelsSelected,
                    displayDropdown: false,
                },
            ],
        };
    },
    computed: {
        ...userMapState({ idCurrentUser: 'id' }),
        ...activitiesMapGetters({
            metas: 'getMetas',
            isTableLoading: 'getLoadingTable',
            appliedFilters: 'getFilters',
            totalActivities: 'getTotalActivitiesInDB',
        }),
        filtersEnabled() {
            if (this.activeTab === TABS.LATE) {
                return this.filters.filter((filter) => filter.code !== 'due_date');
            }
            return this.filters;
        },
        getActiveFilters() {
            return this.filtersEnabled.filter((filter) => filter.isActive);
        },
    },
    watch: {
        activeTab: {
            handler(newTab) {
                if (newTab === TABS.LATE) {
                    const filter = this.filters.find((filter) => filter.code === 'due_date');
                    filter.optionsSelected = null;
                    filter.datePickerValues = [];
                    filter.value = {
                        start: '',
                        end: '',
                    };
                    filter.isActive = false;
                }
            },
        },
    },
    async created() {
        EventBus.on('activities-listing-table:resetFilters', this.resetFilters);
        this.metasLabels = initializeMetasLabels(this.metas);
        if (Feature.isEnabled('activity-listing-crm-follow-up-deal-priotization-agenda-events')) {
            const entitiesFilter = this.filters.find((filter) => filter.code === 'entities');
            entitiesFilter.value.push('event');
            entitiesFilter.options.push({
                label: this.$l10n.__('Événements'),
                id: 'calendar_event',
                icon: 'obj-slsy-calendar',
            });
            this.metasLabels = {
                ...this.metasLabels,
                event: this.metas.event.labels,
            };
        }

        this.staffList = this.metas.authors;
        const filter = this.filters.find((filter) => filter.code === 'labels');
        await initLabelsFilter(this.metasLabels, filter);

        const options = await this.populateStaffOptions();

        const staffFilter = this.filters.find((filter) => filter.code === 'staffs');
        staffFilter.optionsSelected = [this.idCurrentUser];
        staffFilter.value = [this.idCurrentUser];
        staffFilter.options = options;

        const persistedFilters = localStorage.getItem(
            `activities-listing:filters_${this.idCurrentUser}`,
        );
        if (persistedFilters) {
            this.initFiltersFromPersistence(JSON.parse(persistedFilters));
        }
    },
    beforeUnmount() {
        EventBus.off('activities-listing-table:resetFilters', this.resetFilters);
    },
    methods: {
        ...mapActions(['updateFilters']),
        initFiltersFromPersistence(filters) {
            for (const filter of filters) {
                if (!has(this.appliedFilters, filter.code)) {
                    continue;
                }

                const filterToInit = this.filters.find((f) => f.code === filter.code);

                if (!filterToInit) {
                    continue;
                }

                filterToInit.isActive = true;
                filterToInit.optionsSelected = filter.optionsSelected;
                filterToInit.value = filter.value;
                filterToInit.displayDropdown = false;

                if (filter.code === 'due_date' && this.appliedFilters.due_date) {
                    filterToInit.value = this.appliedFilters.due_date;
                    filterToInit.datePickerValues = [
                        moment(this.appliedFilters.due_date.start).format('YYYY-MM-DD'),
                        moment(this.appliedFilters.due_date.end).format('YYYY-MM-DD'),
                    ];
                }
            }
            this.updatePersistenceFilters();
        },
        resetFilters() {
            localStorage.removeItem(`activities-listing:filters_${this.idCurrentUser}`);
            this.filters = this.filters.map((filter) => {
                switch (filter.code) {
                    case 'entities':
                        filter.optionsSelected = [];
                        filter.value = [];
                        break;
                    case 'staffs':
                        filter.optionsSelected = [this.idCurrentUser];
                        filter.value = [this.idCurrentUser];
                        break;
                    case 'due_date':
                        filter.optionsSelected = null;
                        filter.datePickerValues = [];
                        filter.value = {
                            start: '',
                            end: '',
                        };
                        break;
                    case 'labels':
                        filter.optionsSelected = [];
                        filter.value = {};
                        break;
                }
                filter.displayDropdown = false;
                filter.isActive = false;
                return filter;
            });
        },
        async updateFilterValues(values, filter) {
            if (filter.code === 'staffs' && values.length === 0) {
                filter.optionsSelected = [this.idCurrentUser];
                filter.value = [this.idCurrentUser];
            } else {
                filter.optionsSelected = values;
                filter.value = values;
            }

            await this.updateAndPersistFilters({
                ...this.appliedFilters,
                [filter.code]: filter.value,
            });
        },
        updatePersistenceFilters() {
            const filters = this.getActiveFilters.map((filter) => {
                return {
                    code: filter.code,
                    value: filter.value,
                    optionsSelected: filter.optionsSelected,
                };
            });
            localStorage.setItem(
                `activities-listing:filters_${this.idCurrentUser}`,
                JSON.stringify(filters),
            );
        },
        updateActiveFilters(filter) {
            if (!filter.isActive) {
                if (filter.code === 'staffs') {
                    this.populateStaffOptions().then((options) => {
                        filter.value = [this.idCurrentUser];
                        filter.optionsSelected = [this.idCurrentUser];
                        filter.options = options;
                        filter.isActive = true;
                    });
                } else {
                    filter.isActive = true;
                }
            }
            setTimeout(() => {
                filter.displayDropdown = true;
            }, 0);
            EventBus.emit('activities-listing-filters:displayDropdown', filter.code);
        },
        async populateStaffOptions() {
            let options = [
                {
                    id: 0,
                    name: this.$l10n.__('Autre'),
                    options: [],
                },
            ];
            for (const result of this.staffList) {
                const staffFormatted = {
                    value: result.id,
                    label: `${result.firstname} ${result.lastname}`,
                    avatar: {
                        initials: `${result.firstname[0]}${result.lastname[0]}`,
                        type: 'default',
                    },
                };
                if (result.groups) {
                    for (const resultGroup of result.groups) {
                        let group = options.find((option) => option.id === resultGroup.id);
                        if (!group) {
                            group = {
                                id: resultGroup.id,
                                name: resultGroup.name,
                                options: [staffFormatted],
                            };
                            options.push(group);
                        } else {
                            group.options.push(staffFormatted);
                        }
                    }
                } else {
                    options.find((option) => option.id === 0).options.push(staffFormatted);
                }
            }
            //we need to have other group at the end of the list
            options = options.concat(options.splice(0, 1));

            return options;
        },
        async removeActiveFilter(filter) {
            filter.isActive = false;
            if (filter.code === 'staffs' && isEqual(filter.optionsSelected, [this.idCurrentUser])) {
                return;
            }
            if (filter.optionsSelected && filter.optionsSelected.length > 0) {
                switch (filter.code) {
                    case 'staffs':
                        await this.updateFilterValues([this.idCurrentUser], filter);
                        break;

                    case 'due_date':
                        filter.optionsSelected = null;
                        filter.datePickerValues = [];
                        filter.value = {
                            start: '',
                            end: '',
                        };
                        break;

                    case 'labels':
                        filter.optionsSelected = [];
                        filter.value = {};
                        break;

                    default:
                        filter.optionsSelected = [];
                        filter.value = [];
                        break;
                }

                const filters = this.appliedFilters;
                delete filters[filter.code];
                await this.updateAndPersistFilters({ ...filters });
            }
        },
        async onDateSelected({ values, option }, filter) {
            filter.optionsSelected = option;

            filter.datePickerValues = values;
            if (values.length === 2) {
                filter.value = {
                    start: moment(values[0]).toISOString(),
                    end: moment(values[1]).endOf('day').toISOString(),
                };
                await this.updateAndPersistFilters({
                    ...this.appliedFilters,
                    due_date: filter.value,
                });
            }
        },
        async onLabelsSelected(values, filter) {
            const selectedLabels = getSelectedLabels(values, filter, this.metasLabels);
            await this.updateAndPersistFilters({ ...this.appliedFilters, labels: selectedLabels });
        },
        async updateAndPersistFilters(newAppliedFilters) {
            await this.updateFilters(newAppliedFilters);
            this.updatePersistenceFilters();
            EventBus.emit('activities-listing-table:refresh');
        },
    },
};
</script>
